import { type LeadAttributeOptionsType, type UsersType } from '@repo/config-services';
import { getUserFullNameByFL } from '@repo/utils';
import { ALL_OPERATORS_CONFIG } from 'advanced-filters/data/comparatorConfig';
import { ALL_COMPARATORS, type ComparatorType } from 'advanced-filters/data/comparators';
import { ALL_DATA_TYPES, type DataTypeType } from 'advanced-filters/data/dataTypes';
import { type KeyValueType, type TimeFilterOptionsType } from 'advanced-filters/store/filter';
import dayjs from 'dayjs';

interface OptionsType {
  label: string;
  value: string;
}
const formatDate = (date: string | undefined): string => {
  if (!date || !dayjs(date).isValid()) return '';
  return dayjs(date).format('MMM DD, YYYY');
};

export const getTimeString = ({
  comparator,
  timeFilter,
}: {
  comparator?: ComparatorType;
  timeFilter: TimeFilterOptionsType;
}): string => {
  if (!timeFilter) return '';

  const {
    filterType, fromTime, toTime, offsetValue,
  } = timeFilter;

  if (comparator === ALL_COMPARATORS.before && filterType === ALL_COMPARATORS.custom) {
    return formatDate(fromTime);
  }
  if (comparator === ALL_COMPARATORS.after && filterType === ALL_COMPARATORS.custom) {
    return formatDate(toTime);
  }
  if (filterType === ALL_COMPARATORS.custom) {
    return `Between ${formatDate(fromTime)} - ${formatDate(toTime)}`;
  }
  if (filterType === ALL_COMPARATORS.next_n_days) {
    return `Next ${offsetValue} days`;
  }
  if (filterType === ALL_COMPARATORS.last_n_days) {
    return `Last ${offsetValue} days`;
  }
  if (filterType === ALL_COMPARATORS.last_n_hours) {
    return `Last ${offsetValue} hours`;
  }
  return ALL_OPERATORS_CONFIG[filterType]?.label ?? '';
};

export const getValueString = (
  dataType: DataTypeType,
  conditionValue: KeyValueType,
  comparator: ComparatorType,
  userMap: Record<string, UsersType>,
  leadStagesOptions: OptionsType[],
  leadSourceOptions: OptionsType[],
  leadAttributeOptions: LeadAttributeOptionsType[],
): string => {
  if (!conditionValue?.value) return '';

  if (dataType === ALL_DATA_TYPES.boolean) {
    return comparator === ALL_COMPARATORS.is_true ? 'True' : 'False';
  }

  if (dataType === ALL_DATA_TYPES.string) {
    return Array.isArray(conditionValue.value)
      ? conditionValue.value.join(' OR ')
      : conditionValue.value;
  }

  if (dataType === ALL_DATA_TYPES.number) {
    return comparator === ALL_COMPARATORS.between
      ? `${conditionValue.value.from} - ${conditionValue.value.to}`
      : conditionValue.value?.toString();
  }

  if (dataType === ALL_DATA_TYPES.date || dataType === ALL_DATA_TYPES.datetime) {
    return getTimeString({ comparator, timeFilter: conditionValue.value?.timeFilter });
  }

  if (dataType === ALL_DATA_TYPES.multi_select || dataType === ALL_DATA_TYPES.dropdown) {
    const values = conditionValue.value?.map((item: { value: string, label?: string }) => {
      const option = leadAttributeOptions.find((opt) => opt.id === item.value);
      return option?.optionName ?? item.label ?? item.value;
    });
    return values?.join(' OR ') ?? '';
  }

  if (dataType === ALL_DATA_TYPES.reference_user) {
    return conditionValue.value?.map(
      (item: { value: string }) => {
        const user = userMap[item?.value];
        return getUserFullNameByFL(user?.firstName, user?.lastName);
      },
    ).join(' OR ') ?? '';
  }

  if (dataType === ALL_DATA_TYPES.reference_lead_stage) {
    return conditionValue.value?.map(
      (item: { value: string }) => leadStagesOptions.find((opt) => opt.value === item?.value)?.label,
    ).join(' OR ') ?? '';
  }

  if (dataType === ALL_DATA_TYPES.reference_lead_source) {
    return conditionValue.value?.map(
      (item: { value: string }) => leadSourceOptions.find((opt) => opt.value === item?.value)?.label,
    ).join(' OR ') ?? '';
  }

  if (dataType === ALL_DATA_TYPES.default) {
    let values: string[];
    if (Array.isArray(conditionValue.value)) {
      values = conditionValue.value.map((item) => {
        if (typeof item === 'object' && item !== null && 'label' in item) {
          return item.label.toString();
        }
        if (typeof item === 'object' && item !== null && 'value' in item) {
          return item.value.toString();
        }
        return item.toString();
      });
    } else {
      values = [conditionValue.value];
    }
    return values.join(' OR ');
  }

  return '';
};
